import { useEffect, useState } from "react";
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Row,
  Col,
  Badge,
} from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck } from "@fortawesome/free-solid-svg-icons";
import api from "../../../axiosConfig";
import { useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import "./css/ToolsTabMobile.css";
import "./css/HistTableStyle.css";
import { popup } from "../../common/popup";

/* ============================================================
   Helper perhitungan otomatis lembar inspeksi
   - parseRequirement("227 ± 0.1") -> { nominal, tol } (simetris)
   - computeRow -> { x_bar, r_value, judgment }
============================================================ */
const parseRequirement = (req) => {
  if (!req) return null;
  const s = String(req).replace(/,/g, ".").trim();

  // simetris: nominal ± tol
  let m = s.match(/^(-?\d+(?:\.\d+)?)\s*(?:±|\+\/-|\+-)\s*(\d+(?:\.\d+)?)$/);
  if (m) {
    const nominal = parseFloat(m[1]);
    const tol = parseFloat(m[2]);
    return { lo: nominal - tol, hi: nominal + tol };
  }

  // asimetris: nominal +a/-b
  m = s.match(
    /^(-?\d+(?:\.\d+)?)\s*\+\s*(\d+(?:\.\d+)?)\s*\/\s*-\s*(\d+(?:\.\d+)?)$/,
  );
  if (m) {
    const nominal = parseFloat(m[1]);
    return { lo: nominal - parseFloat(m[3]), hi: nominal + parseFloat(m[2]) };
  }

  // rentang min-max: ~ , s/d , .. , tanda hubung
  m = s.match(/^(-?\d+(?:\.\d+)?)\s*(?:~|s\/d|\.\.|–|—|-)\s*(-?\d+(?:\.\d+)?)$/);
  if (m) {
    const a = parseFloat(m[1]);
    const b = parseFloat(m[2]);
    return { lo: Math.min(a, b), hi: Math.max(a, b) };
  }

  return null;
};

const toNum = (v) => {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(String(v).replace(",", "."));
  return Number.isFinite(n) ? n : null;
};

// Klasifikasi measurement berupa TEKS (mis. inspeksi VISUAL): "OK" -> OK, "NG" -> NG.
// Mengembalikan "OK" | "NG" | null (bila kosong / tidak dikenali).
const classifyText = (v) => {
  if (v === null || v === undefined) return null;
  const s = String(v).trim().toLowerCase();
  if (s === "") return null;
  if (/^(ng|nok|no ?good|not ?good|reject|fail(ed)?|bad|x)$/.test(s)) return "NG";
  if (/^(ok|good|pass(ed)?|g|✓|v)$/.test(s)) return "OK";
  return null;
};

// Rentang OK: utamakan nominal+toleransi eksplisit (± / +/-), fallback parse teks.
const specFrom = (row) => {
  const nominal = toNum(row.nominal);
  const tp = toNum(row.tol_plus);
  const tm = toNum(row.tol_minus);
  if (nominal !== null && tp !== null && tm !== null) {
    return { lo: nominal - tm, hi: nominal + tp };
  }
  const tolerance = toNum(row.tolerance);
  if (nominal !== null && tolerance !== null) {
    return { lo: nominal - tolerance, hi: nominal + tolerance };
  }
  return parseRequirement(row.requirement);
};

const computeRow = (row, m1, m2, m3) => {
  const vals = [toNum(m1), toNum(m2), toNum(m3)].filter((v) => v !== null);
  let x_bar = null;
  let r_value = null;
  if (vals.length > 0) {
    x_bar = vals.reduce((a, b) => a + b, 0) / vals.length;
    r_value = Math.max(...vals) - Math.min(...vals);
  }
  let judgment = null;
  const spec = specFrom(row);
  if (spec && vals.length > 0) {
    judgment = vals.every((v) => v >= spec.lo - 1e-9 && v <= spec.hi + 1e-9)
      ? "OK"
      : "NG";
  }
  // Fallback teks (inspeksi VISUAL tanpa spesifikasi angka): OK/NG dari huruf.
  if (judgment === null) {
    const texts = [classifyText(m1), classifyText(m2), classifyText(m3)];
    if (texts.some((t) => t === "NG")) judgment = "NG";
    else if (texts.some((t) => t === "OK")) judgment = "OK";
  }
  return { x_bar, r_value, judgment };
};

const fmt = (n) =>
  n === null || n === undefined ? "-" : Number(n).toFixed(3).replace(/\.?0+$/, "");

// Tampilkan measurement: angka -> diformat, teks -> apa adanya, kosong -> "-"
const fmtMeas = (v) => {
  if (v === null || v === undefined || String(v).trim() === "") return "-";
  const n = Number(String(v).replace(",", "."));
  return Number.isFinite(n) ? fmt(n) : String(v);
};

// Rentang OK (standar) dari spec: "lo s/d hi", atau "-" bila tak ada spec numerik.
const rangeText = (row) => {
  const spec = specFrom(row);
  if (!spec) return "-";
  const clean = (x) => Number(Number(x).toFixed(4)).toString();
  return `${clean(spec.lo)} s/d ${clean(spec.hi)}`;
};

// Jumlah kolom measurement yang boleh diisi, dari "Frekuensi Check".
// "1/Point" -> 1, "2/Point" -> 2, "3/Point" -> 3. Tanpa angka -> 3. Dibatasi 1..3.
const pointCount = (freq) => {
  const m = String(freq || "").match(/(\d+)/);
  const n = m ? parseInt(m[1], 10) : 3;
  return Math.min(3, Math.max(1, n || 3));
};

export default function ToolsTab() {
  const [tools, setTools] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState("ALL");
  /* ================= MAINTENANCE DETAIL ================= */
  const [showMaintenanceModal, setShowMaintenanceModal] = useState(false);
  const [savingFinish, setSavingFinish] = useState(false);
  const [selectedTool, setSelectedTool] = useState(null);
  const [uraianMasalah, setUraianMasalah] = useState("");
  const [dikerjakanOleh, setDikerjakanOleh] = useState("");
  const [dicekOleh, setDicekOleh] = useState("");
  const [judgment, setJudgment] = useState("OK");
  const [selectedStockId, setSelectedStockId] = useState("");
  const [filterStatus, setFilterStatus] = useState("ALL");
  const [keyword, setKeyword] = useState("");
  const [filteredTools, setFilteredTools] = useState([]);
  const navigate = useNavigate();
  const [stockTools, setStockTools] = useState([]);
  const [selectedCategoryStock, setSelectedCategoryStock] = useState("ALL");
  const [loadingStock, setLoadingStock] = useState(false);
  const [selectedSector, setSelectedSector] = useState("ALL");
  const [gejalaMasalah, setGejalaMasalah] = useState("");
  const [penyebabMasalah, setPenyebabMasalah] = useState("");
  const [evaluasiPerbaikan, setEvaluasiPerbaikan] = useState("");
  const [keterangan, setKeterangan] = useState("");
  const [showEditModal, setShowEditModal] = useState(false);
  const [editTool, setEditTool] = useState(null);
  /* ================= PERAWATAN = LEMBAR INSPEKSI ================= */
  const [showInspectionModal, setShowInspectionModal] = useState(false);
  const [savingInspection, setSavingInspection] = useState(false);
  const [loadingStandards, setLoadingStandards] = useState(false);
  const [inspectionRows, setInspectionRows] = useState([]);
  const [inspDikerjakan, setInspDikerjakan] = useState("");
  const [inspDicek, setInspDicek] = useState("");
  const [inspKeterangan, setInspKeterangan] = useState("");
  /* ---- riwayat inspeksi ---- */
  const [showInspHistoryModal, setShowInspHistoryModal] = useState(false);
  const [inspHistory, setInspHistory] = useState([]);
  const [inspDetail, setInspDetail] = useState(null);

  const sectorOptions = [
    ...new Set(tools.map((t) => t.sector_name).filter(Boolean)),
  ];
  /* ================= FETCH TOOLS ================= */
  const fetchTools = async () => {
    setLoading(true);
    try {
      const res = await api.get("/maintenance/tools/list");

      let data = res.data || [];

      // ✅ DEFINISI PRIORITY (INI YANG KURANG)
      const priority = (status) => {
        if (status === "BUTUH_DIPERBAIKI") return 1;
        if (status === "SEDANG_DIPERBAIKI") return 2;
        return 3;
      };

      // 🔥 SORTING: tools baru / butuh diperbaiki di atas
      data.sort((a, b) => {
        if (a.maintenance_status !== b.maintenance_status) {
          return (
            priority(a.maintenance_status) - priority(b.maintenance_status)
          );
        }
        // kalau status sama, urutkan berdasarkan waktu terbaru
        return new Date(b.start_time || 0) - new Date(a.start_time || 0);
      });

      setTools(data);
      setFilteredTools(data);
    } catch (err) {
      console.error("FETCH TOOLS ERROR:", err);
    } finally {
      setLoading(false);
    }
  };

  const openEditModal = (tool) => {
    setEditTool({
      id: tool.tool_id,
      part_name: tool.part_name,
      spec: tool.spec,
      category: tool.category?.toUpperCase(),
      sector_name: tool.sector_name,
    });
    setShowEditModal(true);
  };

  const fetchStockTools = async (toolId) => {
    if (!toolId) return;
    setLoadingStock(true);
    try {
      const res = await api.get(`/stock-tools?toolId=${toolId}`);
      setStockTools(res.data?.data || res.data || []);
    } catch (err) {
      console.error("Gagal ambil stock tools:", err);
      setStockTools([]);
    } finally {
      setLoadingStock(false);
    }
  };

  useEffect(() => {
    fetchTools();
  }, []);

  useEffect(() => {
    if (!selectedTool) return;

    if (judgment === "NG") {
      fetchStockTools(selectedTool.id);
    } else {
      setStockTools([]);
      setSelectedStockId("");
    }
  }, [selectedTool, judgment]);

  /* ================= START MAINTENANCE ================= */
  const startMaintenance = async (tool) => {
    if (!tool.maintenance_id) {
      return popup.error("ID maintenance tidak ditemukan");
    }

    try {
      await api.put(`/maintenance/tools/${tool.maintenance_id}/start`);
      fetchTools();
      popup.success("Maintenance dimulai");
    } catch (err) {
      console.error(err);
      popup.error(err.response?.data?.msg || "Gagal memulai maintenance");
    }
  };

  useEffect(() => {
    let data = [...tools];

    // 🔹 Filter sector
    if (selectedSector !== "ALL") {
      data = data.filter((t) => t.sector_name?.trim() === selectedSector);
    }

    // 🔹 Filter kategori
    if (selectedCategory !== "ALL") {
      data = data.filter(
        (t) => t.category?.trim().toUpperCase() === selectedCategory,
      );
    }

    // 🔹 Filter status
    if (filterStatus !== "ALL") {
      data = data.filter((t) => {
        if (t.maintenance_status) return t.maintenance_status === filterStatus;
        return t.tool_status === filterStatus;
      });
    }

    // 🔹 Filter keyword
    if (keyword.trim()) {
      const key = keyword.toLowerCase();
      data = data.filter(
        (t) =>
          t.spec?.toLowerCase().includes(key) ||
          t.part_name?.toLowerCase().includes(key),
      );
    }

    setFilteredTools(data);
  }, [tools, selectedSector, selectedCategory, filterStatus, keyword]);

  /* ================= OPEN MODAL ================= */
  const openMaintenanceModal = (tool) => {
    if (tool.maintenance_status !== "SEDANG_DIPERBAIKI") {
      return popup.alert("Maintenance belum dimulai");
    }

    setSelectedTool(tool);
    setUraianMasalah("");
    setDikerjakanOleh("");
    setDicekOleh("");
    setJudgment("OK");
    setSelectedStockId("");
    setStockTools([]);
    setShowMaintenanceModal(true);
    setGejalaMasalah("");
    setPenyebabMasalah("");
    setEvaluasiPerbaikan("");
    setKeterangan("");
  };

  /* ================= FINISH MAINTENANCE ================= */
  const finishMaintenance = async () => {
    if (savingFinish) return; // cegah double submit saat jaringan lag
    if (!selectedTool || !selectedTool.maintenance_id) {
      popup.error("ID maintenance tools tidak ditemukan");
      console.error("INVALID TOOL:", selectedTool);
      return;
    }

    if (selectedTool.maintenance_status !== "SEDANG_DIPERBAIKI") {
      return popup.alert("Maintenance belum dikerjakan");
    }

    if (!dikerjakanOleh.trim()) {
      return popup.alert("Nama dikerjakan oleh wajib diisi");
    }

    if (!uraianMasalah.trim()) {
      return popup.alert("Keterangan wajib diisi");
    }

    if (judgment === "NG" && !selectedStockId) {
      return popup.alert("Pilih stock tools pengganti");
    }
    if (!gejalaMasalah.trim()) return popup.alert("Gejala masalah wajib diisi");

    if (!penyebabMasalah.trim()) return popup.alert("Penyebab masalah wajib diisi");

    if (!evaluasiPerbaikan.trim())
      return popup.alert("Evaluasi perbaikan wajib diisi");
    if (!keterangan.trim()) {
      return popup.alert("Keterangan wajib diisi");
    }

    setSavingFinish(true);
    try {
      const payload = {
        judgment,
        gejala_masalah: gejalaMasalah,
        penyebab_masalah: penyebabMasalah,
        solution: uraianMasalah,
        evaluasi_perbaikan: evaluasiPerbaikan,
        keterangan,
        fixed_by: dikerjakanOleh,
        dikerjakan_oleh: dikerjakanOleh,
        dicek_oleh: dicekOleh || null,
        tool_stock_id: judgment === "NG" ? Number(selectedStockId) : null,
      };

      console.log("FINISH PAYLOAD:", payload);

      await api.put(
        `/maintenance/tools/${selectedTool.maintenance_id}/status`,
        payload,
      );

      fetchTools();
      setShowMaintenanceModal(false);
      popup.success("Perbaikan selesai");
    } catch (err) {
      console.error("FINISH ERROR:", err.response?.data || err);
      popup.error(err.response?.data?.msg || "Gagal menyelesaikan maintenance");
    } finally {
      setSavingFinish(false);
    }
  };

  /* ================= OPEN MODAL PERAWATAN (INSPEKSI) ================= */
  const openInspectionModal = async (tool) => {
    setSelectedTool({ ...tool, id: tool.id });
    setInspDikerjakan("");
    setInspDicek("");
    setInspKeterangan("");
    setInspectionRows([]);
    setShowInspectionModal(true);
    setLoadingStandards(true);
    try {
      const res = await api.get(`/tools/${tool.id}/standards`);
      const std = res.data || [];
      if (std.length === 0) {
        setShowInspectionModal(false);
        popup.alert(
          "Standar pengukuran tool ini belum diatur oleh admin.\nMinta admin mengisi Standar terlebih dahulu.",
        );
        return;
      }
      setInspectionRows(
        std.map((s) => ({
          no_urut: s.no_urut,
          requirement: s.requirement,
          nominal: s.nominal,
          tolerance: s.tolerance,
          tol_type: s.tol_type,
          tol_plus: s.tol_plus,
          tol_minus: s.tol_minus,
          instrument: s.instrument || "",
          frequency_check: s.frequency_check || "",
          m1: "",
          m2: "",
          m3: "",
        })),
      );
    } catch (err) {
      console.error("LOAD STANDARDS ERROR:", err);
      setShowInspectionModal(false);
      popup.error("Gagal mengambil standar tool");
    } finally {
      setLoadingStandards(false);
    }
  };

  const updateInspRow = (index, field, value) => {
    setInspectionRows((prev) => {
      const copy = [...prev];
      copy[index] = { ...copy[index], [field]: value };
      return copy;
    });
  };

  /* ================= SUBMIT INSPEKSI ================= */
  const submitInspection = async () => {
    if (!selectedTool || !selectedTool.id) return popup.alert("Tools tidak valid");
    if (!inspDikerjakan.trim()) return popup.alert("Dikerjakan oleh wajib diisi");

    const notEmpty = (v) =>
      v !== null && v !== undefined && String(v).trim() !== "";
    const anyFilled = inspectionRows.some(
      (r) => notEmpty(r.m1) || notEmpty(r.m2) || notEmpty(r.m3),
    );
    if (!anyFilled) return popup.alert("Isi minimal satu hasil pengukuran");

    try {
      setSavingInspection(true);
      const payload = {
        dikerjakan_oleh: inspDikerjakan,
        dicek_oleh: inspDicek || null,
        keterangan: inspKeterangan || null,
        rows: inspectionRows.map((r) => ({
          no_urut: r.no_urut,
          requirement: r.requirement,
          nominal: r.nominal,
          tolerance: r.tolerance,
          tol_type: r.tol_type,
          tol_plus: r.tol_plus,
          tol_minus: r.tol_minus,
          instrument: r.instrument,
          frequency_check: r.frequency_check,
          m1: r.m1,
          m2: r.m2,
          m3: r.m3,
        })),
      };

      const res = await api.post(
        `/maintenance/tools/inspection/${selectedTool.id}`,
        payload,
      );

      fetchTools();
      setShowInspectionModal(false);
      popup.success(
        `Inspeksi tersimpan\nHasil keseluruhan: ${res.data?.overall_judgment || "-"}`,
      );
    } catch (err) {
      console.error("INSPECTION ERROR:", err.response?.data || err);
      popup.error(err.response?.data?.msg || "Gagal menyimpan inspeksi");
    } finally {
      setSavingInspection(false);
    }
  };

  /* ================= RIWAYAT INSPEKSI ================= */
  const openInspHistory = async (tool) => {
    setSelectedTool({ ...tool, id: tool.id });
    setInspHistory([]);
    setInspDetail(null);
    setShowInspHistoryModal(true);
    try {
      const res = await api.get(`/maintenance/tools/inspection/list/${tool.id}`);
      setInspHistory(res.data || []);
    } catch (err) {
      console.error("INSP HISTORY ERROR:", err);
      popup.error("Gagal mengambil riwayat inspeksi");
    }
  };

  const openInspDetail = async (inspectionId) => {
    try {
      const res = await api.get(
        `/maintenance/tools/inspection/detail/${inspectionId}`,
      );
      setInspDetail(res.data);
    } catch (err) {
      console.error("INSP DETAIL ERROR:", err);
      popup.error("Gagal mengambil detail inspeksi");
    }
  };

  /* ================= EXPORT EXCEL ================= */
  const safeName = (s) =>
    (s || "tool").toString().replace(/[\\/:*?"<>|]/g, "-").slice(0, 60);

  // Export daftar seluruh riwayat pengecekan tool terpilih
  const exportInspHistory = () => {
    if (!inspHistory.length) return popup.alert("Belum ada data untuk diexport");

    const toolLabel = selectedTool
      ? `${selectedTool.part_name || ""} (${selectedTool.spec || "-"})`
      : "";

    const wsData = [
      ["Riwayat Pengecekan Tool"],
      ["Tool", toolLabel],
      [],
      ["No", "Tanggal", "Dikerjakan Oleh", "Dicek Oleh", "Hasil", "Keterangan"],
      ...inspHistory.map((h, i) => [
        i + 1,
        formatDateTime(h.inspected_at),
        h.dikerjakan_oleh || "-",
        h.dicek_oleh || "-",
        h.overall_judgment || "-",
        h.keterangan || "-",
      ]),
    ];

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet(wsData);
    XLSX.utils.book_append_sheet(wb, ws, "Riwayat");
    const out = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    saveAs(
      new Blob([out], { type: "application/octet-stream" }),
      `riwayat_pengecekan_${safeName(selectedTool?.part_name)}.xlsx`,
    );
  };

  // Export satu lembar inspeksi (sesuai format form pengukuran)
  const exportInspDetail = () => {
    if (!inspDetail) return;
    const d = inspDetail;
    const toolLabel = `${d.part_name || ""} (${d.spec || "-"})`;

    const wsData = [
      ["Lembar Pengecekan / Inspeksi Tool"],
      ["Tool", toolLabel],
      ["Sector", d.sector_name || "-", "", "Kategori", d.category || "-"],
      ["Tanggal", formatDateTime(d.inspected_at)],
      ["Dikerjakan Oleh", d.dikerjakan_oleh || "-", "", "Dicek Oleh", d.dicek_oleh || "-"],
      ["Hasil Keseluruhan", d.overall_judgment || "-"],
      [],
      [
        "No",
        "Requirement",
        "Instrument",
        "Frekuensi Check",
        "Meas 1",
        "Meas 2",
        "Meas 3",
        "X̄",
        "R",
        "Judgment",
      ],
      ...(d.details || []).map((r) => {
        // measurement: angka -> Number (agar bisa dihitung di Excel), teks -> string
        const meas = (v) => {
          if (v === null || v === undefined || String(v).trim() === "") return "-";
          const n = Number(String(v).replace(",", "."));
          return Number.isFinite(n) ? n : String(v);
        };
        return [
          r.no_urut,
          r.requirement || "-",
          r.instrument || "-",
          r.frequency_check || "-",
          meas(r.m1),
          meas(r.m2),
          meas(r.m3),
          r.x_bar === null ? "-" : Number(r.x_bar),
          r.r_value === null ? "-" : Number(r.r_value),
          r.judgment || "-",
        ];
      }),
    ];

    if (d.keterangan) {
      wsData.push([]);
      wsData.push(["Keterangan", d.keterangan]);
    }

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet(wsData);
    XLSX.utils.book_append_sheet(wb, ws, "Inspeksi");
    const out = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    saveAs(
      new Blob([out], { type: "application/octet-stream" }),
      `inspeksi_${safeName(d.part_name)}_${d.id}.xlsx`,
    );
  };

  // Print satu lembar inspeksi dgn format FM-3005 (Form Pemeriksaan Jig & Moulding)
  const printInspDetail = () => {
    if (!inspDetail) return;
    const d = inspDetail;
    const esc = (v) =>
      v === null || v === undefined
        ? ""
        : String(v).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
    const cell = (v) => {
      if (v === null || v === undefined || String(v).trim() === "") return "";
      const n = Number(String(v).replace(",", "."));
      return Number.isFinite(n) ? fmt(n) : esc(String(v));
    };
    // Untuk baris yang ADA datanya: sel kosong diisi "-".
    const cellDash = (v) => {
      const out = cell(v);
      return out === "" ? "-" : out;
    };
    const txtDash = (v) =>
      v === null || v === undefined || String(v).trim() === ""
        ? "-"
        : esc(String(v));
    const isOK = d.overall_judgment === "OK";

    const rowsHtml = (d.details || [])
      .map(
        (r) => `
        <tr>
          <td>${esc(r.no_urut)}</td>
          <td class="txt">${txtDash(r.requirement)}</td>
          <td>${txtDash(r.instrument)}</td>
          <td>${txtDash(r.frequency_check)}</td>
          <td>${cellDash(r.m1)}</td>
          <td>${cellDash(r.m2)}</td>
          <td>${cellDash(r.m3)}</td>
          <td>${cellDash(r.x_bar)}</td>
          <td>${cellDash(r.r_value)}</td>
          <td>${txtDash(r.judgment)}</td>
        </tr>`,
      )
      .join("");

    // baris kosong tambahan agar SELALU 20 baris (mirip form pra-cetak)
    let extra = "";
    const filled = (d.details || []).length;
    const need = Math.max(0, 20 - filled);
    for (let i = 0; i < need; i++) {
      extra += `<tr>${"<td>&nbsp;</td>".repeat(10)}</tr>`;
    }

    const html = `<!doctype html><html><head><meta charset="utf-8">
<title>FM-3005 - ${esc(d.part_name)}</title>
<style>
  @page { size: A4 portrait; margin: 8mm; }
  * { box-sizing: border-box; }
  html, body { height: 100%; }
  body { font-family: Arial, sans-serif; font-size: 11px; color:#000; margin:0; }

  /* ============================================================
     SISTEM BORDER SERAGAM 1px
     Frame hanya menggambar garis ATAS + KIRI. Setiap sel hanya
     menggambar garis KANAN + BAWAH. Hasilnya SEMUA garis tepat 1px,
     tidak ada yang dobel/menebal, walau tiap blok tabelnya terpisah.
     ============================================================ */
  .frame { border-top:1px solid #000; border-left:1px solid #000; padding: 0; height: 265mm; display: flex; flex-direction: column; }
  .grow { flex: 1 1 auto; min-height: 0; display: flex; flex-direction: column; }
  table { border-collapse: collapse; width: 100%; }

  .company { font-size: 18px; font-weight: bold; margin: 0 0 3px 2px; }
  .title { font-size: 14px; font-weight: bold; text-align:center; }
  .subtitle { font-size: 13px; text-align:center; }

  /* Header: judul + kotak tanda tangan */
  .head td { padding: 4px 8px; vertical-align: middle; border-right:1px solid #000; border-bottom:1px solid #000; }
  .head td.signtd { padding: 0; border-right:0; border-bottom:0; }
  .sign { width:100%; }
  .sign td { border-right:1px solid #000; border-bottom:1px solid #000; text-align:center; font-size:10px; }
  .sign .lbl { height:16px; } .sign .box { height:46px; }

  /* Info: Nama Part / Tanggal / No Kode / PIC */
  .info td { padding: 3px 6px; vertical-align: top; border-bottom: 1px solid #000; }
  .info .k { width: 90px; }
  .info .s { width: 10px; }
  .info .v { border-right: 1px solid #000; }

  /* Sketch */
  .sketch { height: 100%; min-height: 150px; padding:4px; border-right:1px solid #000; border-bottom:1px solid #000; position:relative; overflow:hidden; }
  .sketch img { position:absolute; top:6px; left:6px; right:6px; bottom:6px; margin:auto; max-width:calc(100% - 12px); max-height:calc(100% - 12px); object-fit:contain; }

  /* Tabel pengukuran */
  .meas th, .meas td { border-right:1px solid #000; border-bottom:1px solid #000; padding: 3px 4px; text-align:center; font-size:10px; }
  .meas td.txt { text-align:left; white-space:nowrap; }

  /* Footer: judgement + comment */
  .foot-tbl { width:100%; margin:0; }
  .foot-tbl > tbody > tr > td { border-right:1px solid #000; border-bottom:1px solid #000; vertical-align:top; }
  .jhead { text-align:center; font-weight:bold; padding:3px 4px; }
  .jcell { width:16%; text-align:center; font-size:24px; font-weight:bold; vertical-align:middle; padding:12px 0; }
  .jcircle { display:inline-block; border:2px solid #000; border-radius:50%; padding:2px 10px; line-height:1; }
  .crcell { padding:0; }
  .crhead { font-weight:bold; padding:4px 6px; }
  .cropts { width:100%; }
  .cropts td { font-size:11px; padding:4px 6px; white-space:nowrap; }
  .crremark { border-top:1px solid #000; min-height:34px; padding:4px 6px; white-space:pre-wrap; }
  .docfoot { display:flex; justify-content:space-between; padding:4px 2px 0; font-size:11px; font-weight:bold; }
  .no-print { text-align:center; margin:10px; }
  @media print { .no-print { display:none; } }
</style></head><body>
<div class="no-print">
  <button onclick="window.print()">🖨 Cetak</button>
</div>
<div class="company">PT. Cable Tech</div>
<div class="frame">
  <table class="head">
    <tr>
      <td style="width:60%">
        <div class="title">Laporan Pemeriksaan Jig dan Moulding</div>
        <div class="subtitle">模治具檢驗表</div>
      </td>
      <td class="signtd" style="width:40%">
        <table class="sign">
          <tr><td class="box"></td><td class="box"></td><td class="box"></td></tr>
          <tr><td class="lbl">Issued</td><td class="lbl">Checked</td><td class="lbl">Approved</td></tr>
        </table>
      </td>
    </tr>
  </table>

  <table class="info">
    <tr>
      <td class="k">Nama Part</td><td class="s">:</td><td class="v" style="width:40%">${esc(d.part_name || "")}</td>
      <td class="k">Tanggal</td><td class="s">:</td><td class="v">${esc(formatDateTime(d.inspected_at))}</td>
    </tr>
    <tr>
      <td class="k">No Kode</td><td class="s">:</td><td class="v">${esc(d.kode || "")}</td>
      <td class="k">PIC</td><td class="s">:</td><td class="v">${esc(d.dikerjakan_oleh || "")}</td>
    </tr>
  </table>

  <div class="grow" style="padding:0;">
    <div class="sketch" style="flex:1 1 auto; position:relative;">
      <div style="position:absolute; top:3px; left:5px; font-weight:bold; z-index:2;">Sketch</div>
      ${
        d.photo
          ? `<img src="${d.photo}" style="position:absolute;top:6px;left:6px;right:6px;bottom:6px;margin:auto;max-width:calc(100% - 12px);max-height:calc(100% - 12px);object-fit:contain;" />`
          : ""
      }
    </div>
  </div>

  <div style="padding:0;">
    <table class="meas">
      <thead>
        <tr>
          <th rowspan="2">No.</th>
          <th rowspan="2">Requirement</th>
          <th rowspan="2">instrument</th>
          <th rowspan="2">Frekuensi Check</th>
          <th colspan="3">Measurement</th>
          <th rowspan="2">X&#772;</th>
          <th rowspan="2">R</th>
          <th rowspan="2">Judgement</th>
        </tr>
        <tr><th>1</th><th>2</th><th>3</th></tr>
      </thead>
      <tbody>
        ${rowsHtml}
        ${extra}
      </tbody>
    </table>
  </div>

  <table class="foot-tbl">
    <tr>
      <td class="jhead" colspan="2" style="width:32%">Judgement</td>
      <td rowspan="2" class="crcell">
        <div class="crhead">Comment / Remark : <span style="font-weight:normal;">${esc(d.keterangan || "")}</span></div>
        <table class="cropts">
          <tr>
            <td>&#9744; Repair</td>
            <td>&#9744; Membuat Baru</td>
            <td>&#9744; After Repair</td>
          </tr>
          <tr>
            <td>&#9744; Scrap</td>
            <td>&#9744; Tidak Dipakai Lagi</td>
            <td></td>
          </tr>
        </table>
      </td>
    </tr>
    <tr>
      <td class="jcell">${isOK ? '<span class="jcircle">OK</span>' : "OK"}</td>
      <td class="jcell">${!isOK ? '<span class="jcircle">NG</span>' : "NG"}</td>
    </tr>
  </table>

</div>
<div class="docfoot">
  <span>No Dok : FM-3005</span>
  <span>Revisi : 05 (tgl 03-07-2025)</span>
</div>
<script>window.onload=function(){setTimeout(function(){window.print();},300);};</script>
</body></html>`;

    const w = window.open("", "_blank", "width=900,height=700");
    if (!w) {
      popup.alert("Popup diblokir browser. Izinkan popup untuk mencetak.");
      return;
    }
    w.document.open();
    w.document.write(html);
    w.document.close();
  };

  /* ================= UTILITY ================= */
  const formatDateTime = (date) =>
    date
      ? new Date(date).toLocaleDateString("id-ID", {
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
        })
      : "-";

  const renderStatusBadge = (status) => {
    if (!status) {
      return <Badge bg="success">OK</Badge>; // default tool baru
    }

    switch (status) {
      case "BUTUH_DIPERBAIKI":
        return <Badge bg="danger">BUTUH DIPERBAIKI</Badge>;

      case "SEDANG_DIPERBAIKI":
        return <Badge bg="warning">SEDANG DIPERBAIKI</Badge>;

      case "OK":
        return <Badge bg="success">OK</Badge>;

      case "NG":
        return <Badge bg="danger">NG</Badge>;

      default:
        return <Badge bg="secondary">-</Badge>;
    }
  };

  return (
    <>
      {/* ================= MODAL EDIT TOOL ================= */}
      <Modal
        show={showEditModal}
        onHide={() => setShowEditModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Edit Tool</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {editTool && (
            <Form>
              <Form.Group className="mb-2">
                <Form.Label>Part Name</Form.Label>
                <Form.Control
                  value={editTool.part_name}
                  onChange={(e) =>
                    setEditTool({ ...editTool, part_name: e.target.value })
                  }
                />
              </Form.Group>

              <Form.Group className="mb-2">
                <Form.Label>Kode / Spec</Form.Label>
                <Form.Control
                  value={editTool.spec}
                  onChange={(e) =>
                    setEditTool({ ...editTool, spec: e.target.value })
                  }
                />
              </Form.Group>

              <Form.Group className="mb-2">
                <Form.Label>Kategori</Form.Label>
                <Form.Select
                  value={editTool.category}
                  onChange={(e) =>
                    setEditTool({ ...editTool, category: e.target.value })
                  }
                >
                  <option value="JIG">JIG</option>
                  <option value="NOZEL">NOZEL</option>
                  <option value="MOULDING">MOULDING</option>
                  <option value="DIES">DIES</option>
                  <option value="PIN">PIN</option>
                  <option value="HOLE">HOLE</option>
                  <option value="BRADING">BRADING</option>
                  <option value="PEMILIN">PEMILIN</option>
                </Form.Select>
              </Form.Group>

              <Form.Group className="mb-2">
                <Form.Label>Sector</Form.Label>
                <Form.Select
                  value={editTool.sector_name}
                  onChange={(e) =>
                    setEditTool({ ...editTool, sector_name: e.target.value })
                  }
                >
                  <option value="">-- Pilih Sector --</option>
                  {sectorOptions.map((sec) => (
                    <option key={sec} value={sec}>
                      {sec}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Form>
          )}
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowEditModal(false)}>
            Batal
          </Button>
          <Button
            variant="primary"
            onClick={async () => {
              try {
                await api.put(`/tools/${editTool.id}`, editTool);
                setShowEditModal(false);
                fetchTools();
                popup.success("Tool berhasil diperbarui");
              } catch (err) {
                console.error(err);
                popup.error("Gagal update tool");
              }
            }}
          >
            Simpan
          </Button>
        </Modal.Footer>
      </Modal>

      <Card className="shadow-sm tools-card">
        <Card.Header className="tools-header d-flex justify-content-between align-items-center">
          <h5 className="mb-0 tools-title">
            Daftar Tools
          </h5>
          <div className="tools-filters">
            <div className="d-flex gap-2">
              <Form.Control
                type="text"
                placeholder="🔍 Cari..."
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
              />
              <Form.Select
                value={selectedSector}
                onChange={(e) => setSelectedSector(e.target.value)}
              >
                <option value="ALL">Semua Sector</option>
                {[
                  ...new Set(tools.map((t) => t.sector_name).filter(Boolean)),
                ].map((sec) => (
                  <option key={sec} value={sec}>
                    {sec}
                  </option>
                ))}
              </Form.Select>
            </div>

            <div className="d-flex gap-2">
              <Form.Select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
              >
                <option value="ALL">Semua Kategori</option>
                <option value="JIG">JIG</option>
                <option value="NOZEL">NOZEL</option>
                <option value="MOULDING">MOULDING</option>
                <option value="DIES">DIES</option>
                <option value="PIN">PIN</option>
                <option value="HOLE">HOLE</option>
                <option value="BRADING">BRADING</option>
                <option value="PEMILIN">PEMILIN</option>
              </Form.Select>
              <Form.Select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
              >
                <option value="ALL">Semua Status</option>
                <option value="BUTUH_DIPERBAIKI">Butuh Diperbaiki</option>
                <option value="SEDANG_DIPERBAIKI">Sedang Diperbaiki</option>
                <option value="OK">OK</option>
                <option value="NG">NG</option>
              </Form.Select>
            </div>
          </div>
        </Card.Header>

        <Card.Body style={{ padding: 0, flex: 1, overflow: "hidden" }}>
          <div style={{ height: "100%", overflow: "auto" }}>
            <table className="hist-table tools-hist text-nowrap mb-0">
              <thead
                className="table-light"
                style={{
                  position: "sticky",
                  top: 0,
                  background: "#fff",
                  zIndex: 1,
                }}
              >
                <tr>
                  <th
                    style={{
                      width: "40px",
                      background: "#1e3a8a",
                      color: "#fff",
                    }}
                  >
                    No
                  </th>
                  <th style={{ background: "#1e3a8a", color: "#fff" }}>
                    Sector
                  </th>
                  <th style={{ background: "#1e3a8a", color: "#fff" }}>
                    Kategori
                  </th>
                  <th style={{ background: "#1e3a8a", color: "#fff" }}>
                    Kode / spec
                  </th>
                  <th style={{ background: "#1e3a8a", color: "#fff" }}>
                    Part Name
                  </th>
                  <th style={{ background: "#1e3a8a", color: "#fff" }}>
                    Status
                  </th>
                  <th
                    style={{
                      background: "#1e3a8a",
                      color: "#fff",
                      whiteSpace: "nowrap",
                    }}
                  >
                    Last Maintenance
                  </th>
                  <th
                    style={{
                      width: "250px",
                      background: "#1e3a8a",
                      color: "#fff",
                    }}
                  >
                    Aksi
                  </th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={8} className="text-center py-3">
                      Loading...
                    </td>
                  </tr>
                ) : filteredTools.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="text-center py-3">
                      Data belum ada
                    </td>
                  </tr>
                ) : (
                  filteredTools.map((t, i) => (
                    <tr key={t.id}>
                      <td>{i + 1}</td>
                      <td>{t.sector_name}</td>
                      <td>{t.category || "-"}</td>
                      <td className="cell-left">{t.spec}</td>
                      <td className="cell-left">{t.part_name}</td>
                      <td>
                        {renderStatusBadge(
                          t.maintenance_status
                            ? t.maintenance_status
                            : t.tool_status,
                        )}
                      </td>

                      <td>{t.end_time ? formatDateTime(t.end_time) : "-"}</td>

                      <td>
                        <div className="d-flex flex-row flex-nowrap justify-content-center align-items-center gap-1">
                          {/* Tombol Kerjakan */}
                          {t.maintenance_status === "BUTUH_DIPERBAIKI" && (
                            <Button
                              size="sm"
                              variant="warning"
                              onClick={() => startMaintenance(t)}
                            >
                              Kerjakan
                            </Button>
                          )}

                          {/* Tombol Selesai hanya muncul kalau sedang diperbaiki */}
                          {t.maintenance_status === "SEDANG_DIPERBAIKI" && (
                            <Button
                              size="sm"
                              variant="success"
                              onClick={() => openMaintenanceModal(t)}
                            >
                              <FontAwesomeIcon icon={faCheck} /> Selesai
                            </Button>
                          )}

                          {/* Perawatan = lembar inspeksi pengukuran.
                              Hanya untuk tools yang tidak sedang dalam alur perbaikan */}
                          {t.maintenance_status !== "BUTUH_DIPERBAIKI" &&
                            t.maintenance_status !== "SEDANG_DIPERBAIKI" && (
                              <>
                                <Button
                                  size="sm"
                                  variant="primary"
                                  onClick={() => openInspectionModal(t)}
                                >
                                  🔍 Pengecekan
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline-secondary"
                                  onClick={() => openInspHistory(t)}
                                >
                                  📄 Inspeksi
                                </Button>
                              </>
                            )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </Card.Body>
      </Card>

      {/* ================= MODAL MAINTENANCE ================= */}
      <Modal
        show={showMaintenanceModal}
        onHide={() => setShowMaintenanceModal(false)}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Detail Maintenance Tools</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <Form>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>
                    Dikerjakan Oleh <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    value={dikerjakanOleh}
                    onChange={(e) => setDikerjakanOleh(e.target.value)}
                    placeholder="Nama yang mengerjakan"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Dicek Oleh</Form.Label>
                  <Form.Control
                    value={dicekOleh}
                    onChange={(e) => setDicekOleh(e.target.value)}
                    placeholder="Nama yang mengecek (opsional)"
                  />
                </Form.Group>
              </Col>
            </Row>
            <Form.Group className="mb-3">
              <Form.Label>Judgment</Form.Label>
              <Form.Select
                value={judgment}
                onChange={(e) => setJudgment(e.target.value)}
              >
                <option value="OK">OK</option>
                <option value="NG">NG</option>
              </Form.Select>
            </Form.Group>
            {judgment === "NG" && (
              <div className="mt-3">
                <h6>Stock Tools Terkait</h6>

                {loadingStock && (
                  <div className="text-muted mb-2">Loading stock tools...</div>
                )}

                {!loadingStock && stockTools.length === 0 && (
                  <div className="text-muted mb-2">
                    Tidak ada stock tools tersedia
                  </div>
                )}

                {/* Dropdown Kategori */}
                <Form.Group className="mb-2">
                  <Form.Label>Kategori Stock</Form.Label>
                  <Form.Select
                    value={selectedCategoryStock}
                    onChange={(e) => {
                      setSelectedCategoryStock(e.target.value);
                      setSelectedStockId("");
                    }}
                  >
                    <option value="ALL">Semua Kategori</option>
                    <option value="JIG">JIG</option>
                    <option value="NOZEL">NOZEL</option>
                    <option value="MOULDING">MOULDING</option>
                    <option value="DIES">DIES</option>
                    <option value="PIN">PIN</option>
                    <option value="HOLE">HOLE</option>
                    <option value="BRADING">BRADING</option>
                    <option value="PEMILIN">PEMILIN</option>
                  </Form.Select>
                </Form.Group>

                {/* Dropdown Stock */}
                <Form.Group>
                  <Form.Label>Pilih Stock Tool</Form.Label>
                  <Form.Select
                    value={selectedStockId}
                    onChange={(e) => setSelectedStockId(e.target.value)}
                    disabled={loadingStock || stockTools.length === 0}
                  >
                    <option value="">-- Pilih Stock Tool --</option>

                    {stockTools
                      .filter(
                        (t) =>
                          selectedCategoryStock === "ALL" ||
                          String(t.category || "").toUpperCase() ===
                            selectedCategoryStock,
                      )
                      .map((t) => (
                        <option
                          key={t.id}
                          value={t.id}
                          disabled={t.stock_qty <= 0}
                        >
                          || {t.spec} || {t.part_name} || (Stock: {t.stock_qty})
                        </option>
                      ))}
                  </Form.Select>
                </Form.Group>
              </div>
            )}

            <Form.Group className="mb-3">
              <Form.Label>Gejala Masalah *</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={gejalaMasalah}
                onChange={(e) => setGejalaMasalah(e.target.value)}
                placeholder="Gejala Masalah"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Uraian & Penyebab Masalah *</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={penyebabMasalah}
                onChange={(e) => setPenyebabMasalah(e.target.value)}
                placeholder="Uraian & penyebab Masalah"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Tindakan / Penyelesaian *</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={uraianMasalah}
                onChange={(e) => setUraianMasalah(e.target.value)}
                placeholder="Tindakan / Penyelesaian"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Evaluasi Perbaikan *</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={evaluasiPerbaikan}
                onChange={(e) => setEvaluasiPerbaikan(e.target.value)}
                placeholder="Evaluasi Perbaikan"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>
                Keterangan <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={keterangan}
                onChange={(e) => setKeterangan(e.target.value)}
                placeholder="Keterangan"
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal
          show={showEditModal}
          onHide={() => setShowEditModal(false)}
          centered
        >
          <Modal.Header closeButton>
            <Modal.Title>Edit Tool</Modal.Title>
          </Modal.Header>

          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowEditModal(false)}>
              Batal
            </Button>
            <Button
              variant="success"
              onClick={async () => {
                try {
                  await api.put(`/tools/${editTool.id}`, editTool);
                  setShowEditModal(false);
                  fetchTools();
                  popup.success("Tool berhasil diperbarui");
                } catch (err) {
                  console.error(err);
                  popup.error("Gagal update tool");
                }
              }}
            >
              Simpan
            </Button>
          </Modal.Footer>
        </Modal>

        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowMaintenanceModal(false)}
          >
            Batal
          </Button>
          <Button variant="success" onClick={finishMaintenance} disabled={savingFinish}>
            {savingFinish ? "Menyimpan..." : "Simpan & Selesai"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ================= MODAL PERAWATAN = LEMBAR INSPEKSI ================= */}
      <Modal
        show={showInspectionModal}
        onHide={() => setShowInspectionModal(false)}
        size="xl"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            Perawatan / Inspeksi Tool
            {selectedTool
              ? ` - ${selectedTool.part_name || ""} (${selectedTool.spec || "-"})`
              : ""}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {loadingStandards ? (
            <div className="text-center py-3">Memuat standar...</div>
          ) : (
            <>
              <Row className="mb-3">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>
                      Dikerjakan Oleh <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      value={inspDikerjakan}
                      onChange={(e) => setInspDikerjakan(e.target.value)}
                      placeholder="Nama yang mengerjakan"
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Dicek Oleh</Form.Label>
                    <Form.Control
                      value={inspDicek}
                      onChange={(e) => setInspDicek(e.target.value)}
                      placeholder="Nama yang mengecek (opsional)"
                    />
                  </Form.Group>
                </Col>
              </Row>

              <div style={{ overflowX: "auto" }}>
                <Table bordered size="sm" className="text-center align-middle">
                  <thead>
                    <tr>
                      <th
                        rowSpan={2}
                        style={{ background: "#1e3a8a", color: "#fff" }}
                      >
                        No
                      </th>
                      <th
                        rowSpan={2}
                        style={{ background: "#1e3a8a", color: "#fff" }}
                      >
                        Requirement
                      </th>
                      <th
                        rowSpan={2}
                        style={{ background: "#1e3a8a", color: "#fff" }}
                      >
                        Instrument
                      </th>
                      <th
                        rowSpan={2}
                        style={{ background: "#1e3a8a", color: "#fff" }}
                      >
                        Frekuensi Check
                      </th>
                      <th
                        rowSpan={2}
                        style={{ background: "#1e3a8a", color: "#fff" }}
                      >
                        Standar
                        <br />
                        (Rentang OK)
                      </th>
                      <th
                        colSpan={3}
                        style={{ background: "#1e3a8a", color: "#fff" }}
                      >
                        Measurement
                      </th>
                      <th
                        rowSpan={2}
                        style={{ background: "#1e3a8a", color: "#fff" }}
                      >
                        X̄
                      </th>
                      <th
                        rowSpan={2}
                        style={{ background: "#1e3a8a", color: "#fff" }}
                      >
                        R
                      </th>
                      <th
                        rowSpan={2}
                        style={{ background: "#1e3a8a", color: "#fff" }}
                      >
                        Judgment
                      </th>
                    </tr>
                    <tr>
                      <th style={{ background: "#274aa8", color: "#fff" }}>1</th>
                      <th style={{ background: "#274aa8", color: "#fff" }}>2</th>
                      <th style={{ background: "#274aa8", color: "#fff" }}>3</th>
                    </tr>
                  </thead>
                  <tbody>
                    {inspectionRows.map((r, i) => {
                      const nPoint = pointCount(r.frequency_check);
                      const c = computeRow(
                        r,
                        r.m1,
                        nPoint >= 2 ? r.m2 : "",
                        nPoint >= 3 ? r.m3 : "",
                      );
                      return (
                        <tr key={i}>
                          <td>{r.no_urut || i + 1}</td>
                          <td style={{ whiteSpace: "nowrap" }}>
                            {r.requirement}
                          </td>
                          <td>{r.instrument || "-"}</td>
                          <td>{r.frequency_check || "-"}</td>
                          <td style={{ whiteSpace: "nowrap" }}>
                            {rangeText(r)}
                          </td>
                          {["m1", "m2", "m3"].map((f, mi) => {
                            const disabled = mi >= nPoint;
                            return (
                              <td key={f} style={{ minWidth: "80px" }}>
                                <Form.Control
                                  size="sm"
                                  type="text"
                                  value={disabled ? "" : r[f]}
                                  disabled={disabled}
                                  onChange={(e) =>
                                    updateInspRow(i, f, e.target.value)
                                  }
                                  style={
                                    disabled ? { background: "#e9ecef" } : undefined
                                  }
                                />
                              </td>
                            );
                          })}
                          <td>{fmt(c.x_bar)}</td>
                          <td>{fmt(c.r_value)}</td>
                          <td>
                            {c.judgment ? (
                              <Badge
                                bg={c.judgment === "OK" ? "success" : "danger"}
                              >
                                {c.judgment}
                              </Badge>
                            ) : (
                              "-"
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </div>

              <Form.Group className="mt-2">
                <Form.Label>Keterangan</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  value={inspKeterangan}
                  onChange={(e) => setInspKeterangan(e.target.value)}
                  placeholder="Keterangan (opsional)"
                />
              </Form.Group>

              {(() => {
                const overall = inspectionRows.some((r) => {
                  const n = pointCount(r.frequency_check);
                  return (
                    computeRow(
                      r,
                      r.m1,
                      n >= 2 ? r.m2 : "",
                      n >= 3 ? r.m3 : "",
                    ).judgment === "NG"
                  );
                })
                  ? "NG"
                  : "OK";
                return (
                  <div className="mt-3">
                    Hasil keseluruhan:{" "}
                    <Badge bg={overall === "OK" ? "success" : "danger"}>
                      {overall}
                    </Badge>
                  </div>
                );
              })()}
            </>
          )}
        </Modal.Body>

        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowInspectionModal(false)}
            disabled={savingInspection}
          >
            Batal
          </Button>
          <Button
            variant="success"
            onClick={submitInspection}
            disabled={savingInspection || loadingStandards}
          >
            {savingInspection ? "Menyimpan..." : "Simpan Inspeksi"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ================= MODAL RIWAYAT INSPEKSI ================= */}
      <Modal
        show={showInspHistoryModal}
        onHide={() => {
          setShowInspHistoryModal(false);
          setInspDetail(null);
        }}
        size="xl"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            Riwayat Inspeksi
            {selectedTool
              ? ` - ${selectedTool.part_name || ""} (${selectedTool.spec || "-"})`
              : ""}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {!inspDetail ? (
            <>
            <div className="d-flex justify-content-end mb-2">
              <Button
                size="sm"
                variant="success"
                onClick={exportInspHistory}
                disabled={!inspHistory.length}
              >
                ⬇ Export Excel (Daftar)
              </Button>
            </div>
            <Table bordered hover size="sm" className="align-middle">
              <thead>
                <tr>
                  <th style={{ background: "#1e3a8a", color: "#fff" }}>
                    Tanggal
                  </th>
                  <th style={{ background: "#1e3a8a", color: "#fff" }}>
                    Dikerjakan
                  </th>
                  <th style={{ background: "#1e3a8a", color: "#fff" }}>Dicek</th>
                  <th style={{ background: "#1e3a8a", color: "#fff" }}>Hasil</th>
                  <th style={{ background: "#1e3a8a", color: "#fff" }}>Detail</th>
                </tr>
              </thead>
              <tbody>
                {inspHistory.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="text-center py-3">
                      Belum ada inspeksi
                    </td>
                  </tr>
                ) : (
                  inspHistory.map((h) => (
                    <tr key={h.id}>
                      <td>{formatDateTime(h.inspected_at)}</td>
                      <td>{h.dikerjakan_oleh || "-"}</td>
                      <td>{h.dicek_oleh || "-"}</td>
                      <td>
                        <Badge
                          bg={h.overall_judgment === "OK" ? "success" : "danger"}
                        >
                          {h.overall_judgment}
                        </Badge>
                      </td>
                      <td>
                        <Button
                          size="sm"
                          variant="info"
                          onClick={() => openInspDetail(h.id)}
                        >
                          Lihat
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
            </>
          ) : (
            <>
              <div className="d-flex justify-content-between mb-2">
                <Button
                  size="sm"
                  variant="outline-secondary"
                  onClick={() => setInspDetail(null)}
                >
                  ← Kembali
                </Button>
                <div className="d-flex gap-2">
                  <Button size="sm" variant="dark" onClick={printInspDetail}>
                    🖨 Print (FM-3005)
                  </Button>
                  <Button size="sm" variant="success" onClick={exportInspDetail}>
                    ⬇ Export Excel
                  </Button>
                </div>
              </div>
              <div className="mb-2">
                <strong>Tanggal:</strong>{" "}
                {formatDateTime(inspDetail.inspected_at)} &nbsp;|&nbsp;
                <strong> Dikerjakan:</strong> {inspDetail.dikerjakan_oleh || "-"}{" "}
                &nbsp;|&nbsp;
                <strong> Dicek:</strong> {inspDetail.dicek_oleh || "-"}{" "}
                &nbsp;|&nbsp;
                <strong> Hasil:</strong>{" "}
                <Badge
                  bg={inspDetail.overall_judgment === "OK" ? "success" : "danger"}
                >
                  {inspDetail.overall_judgment}
                </Badge>
              </div>
              <div style={{ overflowX: "auto" }}>
                <Table bordered size="sm" className="text-center align-middle">
                  <thead>
                    <tr>
                      <th style={{ background: "#1e3a8a", color: "#fff" }}>No</th>
                      <th style={{ background: "#1e3a8a", color: "#fff" }}>
                        Requirement
                      </th>
                      <th style={{ background: "#1e3a8a", color: "#fff" }}>
                        Instrument
                      </th>
                      <th style={{ background: "#1e3a8a", color: "#fff" }}>
                        Frekuensi
                      </th>
                      <th style={{ background: "#1e3a8a", color: "#fff" }}>1</th>
                      <th style={{ background: "#1e3a8a", color: "#fff" }}>2</th>
                      <th style={{ background: "#1e3a8a", color: "#fff" }}>3</th>
                      <th style={{ background: "#1e3a8a", color: "#fff" }}>X̄</th>
                      <th style={{ background: "#1e3a8a", color: "#fff" }}>R</th>
                      <th style={{ background: "#1e3a8a", color: "#fff" }}>
                        Judgment
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {(inspDetail.details || []).map((d) => (
                      <tr key={d.id}>
                        <td>{d.no_urut}</td>
                        <td style={{ whiteSpace: "nowrap" }}>{d.requirement}</td>
                        <td>{d.instrument || "-"}</td>
                        <td>{d.frequency_check || "-"}</td>
                        <td>{fmtMeas(d.m1)}</td>
                        <td>{fmtMeas(d.m2)}</td>
                        <td>{fmtMeas(d.m3)}</td>
                        <td>{fmt(d.x_bar)}</td>
                        <td>{fmt(d.r_value)}</td>
                        <td>
                          {d.judgment ? (
                            <Badge
                              bg={d.judgment === "OK" ? "success" : "danger"}
                            >
                              {d.judgment}
                            </Badge>
                          ) : (
                            "-"
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
              {inspDetail.keterangan && (
                <div className="mt-2">
                  <strong>Keterangan:</strong> {inspDetail.keterangan}
                </div>
              )}
            </>
          )}
        </Modal.Body>
      </Modal>
    </>
  );
}
